import type { LayoutSlotBox } from "@cva/shared";
import { createContext, useContext } from "react";
import { createStore, type StoreApi, useStore } from "zustand";

/**
 * Playback state of the video editor: playhead, play/pause, selected clip and
 * timeline zoom. Kept apart from the document store, so the 60 fps playhead
 * updates never touch the undo history or the autosave.
 */

/** `video`: main track, `overlay`: the video tracks above it, `audio`: music. */
export type ClipSelection = { track: "video" | "overlay" | "audio"; id: string } | null;

/** Tabs of the captions inspector (right panel). */
export type CaptionTab = "text" | "style";

/** Request to show a caption in the inspector's list (from the timeline or the canvas). */
export interface CaptionFocus {
  /** Key of a word of the caption. */
  key: string;
  /** Start editing its text. */
  edit: boolean;
  seq: number;
}

/** Areas of a screen layout still waiting for a video or image (tab "Layout"). */
export interface PendingLayout {
  /** Name of the layout, for the prompt. */
  layout: string;
  slots: LayoutSlotBox[];
  range: { start: number; end: number };
  total: number;
}

export interface VideoState {
  /** Playhead in ms. */
  time: number;
  playing: boolean;
  /** Changes on every seek by the user (the player then repositions the media). */
  seekVersion: number;
  selectedClip: ClipSelection;
  /** Timeline zoom. */
  pxPerSecond: number;
  /** Drag on the canvas moves the picture of the selected clip. */
  framing: boolean;
  safeZones: boolean;
  captionTab: CaptionTab;
  captionFocus: CaptionFocus | null;
  /** Screen layout areas to fill (from the layout tab or the timeline's context menu). */
  pendingLayout: PendingLayout | null;
  /** Screen layouts with gaps and rounded corners ("Mit Abstand"). */
  layoutSpaced: boolean;
}

export interface VideoActions {
  /** Seek (user action). */
  seek(time: number): void;
  /** Time reported by the player while playing. */
  tick(time: number): void;
  play(): void;
  pause(): void;
  toggle(): void;
  selectClip(selection: ClipSelection): void;
  setZoom(pxPerSecond: number): void;
  setFraming(on: boolean): void;
  toggleSafeZones(): void;
  setCaptionTab(tab: CaptionTab): void;
  /** Opens the text tab at the caption containing the word `key`. */
  focusCaption(key: string, edit?: boolean): void;
  /** The inspector has handled the focus request. */
  clearCaptionFocus(): void;
  setPendingLayout(pending: PendingLayout | null): void;
  setLayoutSpaced(spaced: boolean): void;
}

export type VideoStore = StoreApi<VideoState & VideoActions>;

export function createVideoStore(): VideoStore {
  return createStore<VideoState & VideoActions>()((set, get) => ({
    time: 0,
    playing: false,
    seekVersion: 0,
    selectedClip: null,
    pxPerSecond: 60,
    framing: false,
    safeZones: false,
    captionTab: "text",
    captionFocus: null,
    pendingLayout: null,
    layoutSpaced: false,
    seek(time) {
      set((state) => ({ time: Math.max(0, time), seekVersion: state.seekVersion + 1 }));
    },
    tick(time) {
      if (Math.abs(get().time - time) > 0.5) set({ time });
    },
    play() {
      if (!get().playing) set({ playing: true });
    },
    pause() {
      if (get().playing) set((state) => ({ playing: false, seekVersion: state.seekVersion + 1 }));
    },
    toggle() {
      if (get().playing) get().pause();
      else get().play();
    },
    selectClip(selection) {
      set({ selectedClip: selection, framing: selection ? get().framing : false });
    },
    setZoom(pxPerSecond) {
      set({ pxPerSecond: Math.max(4, Math.min(600, pxPerSecond)) });
    },
    setFraming(on) {
      set({ framing: on });
    },
    toggleSafeZones() {
      set((state) => ({ safeZones: !state.safeZones }));
    },
    setCaptionTab(tab) {
      set({ captionTab: tab });
    },
    focusCaption(key, edit = false) {
      set((state) => ({
        captionTab: "text",
        captionFocus: { key, edit, seq: (state.captionFocus?.seq ?? 0) + 1 },
      }));
    },
    clearCaptionFocus() {
      if (get().captionFocus) set({ captionFocus: null });
    },
    setPendingLayout(pending) {
      set({ pendingLayout: pending });
    },
    setLayoutSpaced(spaced) {
      set({ layoutSpaced: spaced });
    },
  }));
}

export const VideoContext = createContext<VideoStore | null>(null);

/** The video store, or null in design documents. */
export function useVideoApi(): VideoStore | null {
  return useContext(VideoContext);
}

export function useVideo<T>(selector: (state: VideoState & VideoActions) => T): T {
  const store = useContext(VideoContext);
  if (!store) throw new Error("Video-Store fehlt");
  return useStore(store, selector);
}

/** Like `useVideo`, but returns `fallback` outside video documents. */
export function useVideoMaybe<T>(
  selector: (state: VideoState & VideoActions) => T,
  fallback: T,
): T {
  const store = useContext(VideoContext);
  // A constant store keeps the hook order stable when there is none.
  return useStore(store ?? FALLBACK_STORE, (state) => (store ? selector(state) : fallback));
}

const FALLBACK_STORE = createVideoStore();
